import { frontendBuildHttpHeader, getAppVersion } from '../appVersion'
import { getApiUrl } from '../backendUrl'

const appVersion = getAppVersion()

// Global configuration
let globalConfig = {
  baseURL: '',
  headers: {},
  token: null
}

// Request interceptors array
const requestInterceptors = []
const responseInterceptors = []

// Interceptor management functions
export const addRequestInterceptor = (onFulfilled, onRejected) => {
  requestInterceptors.push({ onFulfilled, onRejected })
}

export const addResponseInterceptor = (onFulfilled, onRejected) => {
  responseInterceptors.push({ onFulfilled, onRejected })
}

// Apply request interceptors
const applyRequestInterceptors = async (config) => {
  let modifiedConfig = { ...config }
  
  for (const interceptor of requestInterceptors) {
    try {
      if (interceptor.onFulfilled) {
        modifiedConfig = await interceptor.onFulfilled(modifiedConfig)
      }
    } catch (error) {
      if (interceptor.onRejected) {
        throw await interceptor.onRejected(error)
      }
      throw error
    }
  }
  
  return modifiedConfig
}

// Apply response interceptors
const applyResponseInterceptors = async (response, error = null) => {
  let result = error ? { error } : { response }
  
  for (const interceptor of responseInterceptors) {
    try {
      if (error && interceptor.onRejected) {
        result = { response: await interceptor.onRejected(error) }
        error = null
      } else if (!error && interceptor.onFulfilled) {
        result = { response: await interceptor.onFulfilled(result.response) }
      }
    } catch (interceptorError) {
      result = { error: interceptorError }
      error = interceptorError
    }
  }
  
  if (result.error) {
    throw result.error
  }
  return result.response
}

// Main fetch wrapper function
export const fetchWrapper = async (config) => {
  // Merge with global config
  const mergedConfig = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...globalConfig.headers,
      ...config.headers
    },
    ...config
  }

  // Add auth token if available
  if (globalConfig.token) {
    mergedConfig.headers.Authorization = 'Bearer ' + globalConfig.token
  }

  // Add app version header
  mergedConfig.headers[frontendBuildHttpHeader] = appVersion

  // Apply request interceptors
  const processedConfig = await applyRequestInterceptors(mergedConfig)

  // Build the full URL (mirroring axios combineURLs behavior)
  const url = processedConfig.url.startsWith('http')
    ? processedConfig.url
    : `${globalConfig.baseURL.replace(/\/+$/, '')}/${processedConfig.url.replace(/^\/+/, '')}`

  // Prepare fetch options
  const fetchOptions = {
    method: processedConfig.method,
    headers: processedConfig.headers,
    signal: processedConfig.signal
  }

  // Add body for methods that support it
  if (processedConfig.data && ['POST', 'PUT', 'PATCH'].includes(processedConfig.method.toUpperCase())) {
    fetchOptions.body = typeof processedConfig.data === 'string' 
      ? processedConfig.data 
      : JSON.stringify(processedConfig.data)
  }

  try {
    const response = await fetch(url, fetchOptions)
    
    // Create axios-like response object
    const axiosLikeResponse = {
      data: null,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      config: processedConfig
    }

    // Parse response body if it exists
    if (response.status !== 204) { // No Content
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        axiosLikeResponse.data = await response.json()
      } else {
        axiosLikeResponse.data = await response.text()
      }
    }

    // Handle HTTP error statuses
    if (!response.ok) {
      const error = new Error(`HTTP Error: ${response.status} ${response.statusText}`)
      error.response = axiosLikeResponse
      error.config = processedConfig
      throw error
    }

    // Apply response interceptors
    return await applyResponseInterceptors(axiosLikeResponse)

  } catch (error) {
    // Apply response interceptors for errors
    if (error.name === 'AbortError') {
      const cancelError = new Error('Request was cancelled')
      cancelError.code = 'ECONNABORTED'
      throw await applyResponseInterceptors(null, cancelError)
    }
    throw await applyResponseInterceptors(null, error)
  }
}

// Convenience methods similar to axios
fetchWrapper.get = (url, config = {}) => 
  fetchWrapper({ ...config, url, method: 'GET' })

fetchWrapper.post = (url, data, config = {}) => 
  fetchWrapper({ ...config, url, method: 'POST', data })

fetchWrapper.put = (url, data, config = {}) => 
  fetchWrapper({ ...config, url, method: 'PUT', data })

fetchWrapper.delete = (url, config = {}) => 
  fetchWrapper({ ...config, url, method: 'DELETE' })

// Configure function similar to configureAxios
export default function configureFetch(token) {
  globalConfig.baseURL = getApiUrl()
  globalConfig.token = token

  // Clear existing interceptors
  requestInterceptors.length = 0
  responseInterceptors.length = 0

  // Add request interceptor (equivalent to axios.interceptors.request.use)
  addRequestInterceptor(
    config => {
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = 'Bearer ' + token
      }

      config.headers[frontendBuildHttpHeader] = appVersion

      return config
    },
    error => {
      console.error(error)
      return Promise.reject(error)
    }
  )

  // Add response interceptor (equivalent to axios.interceptors.response.use)
  addResponseInterceptor(
    response => response,
    error => {
      const response = error.response
      if (!response) {
        console.warn('Did not receive response from server')
      } else if (response.status >= 400 && response.status < 500) {
        console.warn(`Client error: ${response.status} ${response.statusText}`, response.headers, response.data)
      } else if (response.status >= 500) {
        console.error(`Server error: ${response.status} ${response.statusText}`, response.headers, response.data)
      }

      return Promise.reject(error)
    }
  )
}